import { Badge, ClickAwayListener, Fade, IconButton, List, Popper } from '@mui/material'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { AsyncJobData, AsyncJobDataType } from '@returnmates/client-core/src/graphql/generated/api'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import Logo from '@returnmates/ui-core/src/components/imageComponents/Logo'
import NotificationIcon from '@returnmates/ui-core/src/components/imageComponents/Notification'
import UserIcon from '@returnmates/ui-core/src/components/imageComponents/User'
import { Auth } from 'aws-amplify'
import clsx from 'clsx'
import { memo, useCallback, useContext, useMemo, useState } from 'react'
import { Range } from 'react-date-range'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { AUDITS, MAIN, OPERATIONS } from '../../constants/paths'
import sidebarItems from '../../constants/sidebarItems'
import { todayDateFilter } from '../../constants/staticRanges'
import {
  AUDITOR_SECTIONS,
  cognitoGroups,
  MANAGER_SECTIONS,
  OPERATIONS_SECTIONS,
  SAVER_SECTIONS,
  WAREHOUSE_SECTIONS,
} from '../../constants/userPermissions'
import { FlyoutSidebarContext } from '../../pages/Main'
import NotificationListItem from './components/NotificationListItem'
import NavLinkWrap from './NavLinkWrap'
import useStyles from './styles'

interface Props {
  userRole: string
  setCurrentDateRange: (range: Range[]) => void
  notifications: Array<AsyncJobData | null> | null
}

const { admin, manager, operations, warehouse, auditor, saver } = cognitoGroups

function Sidebar({ userRole, setCurrentDateRange, notifications }: Props) {
  const location = useLocation()
  const { handleCloseSidebar } = useContext(FlyoutSidebarContext)
  const pathname = useMemo(() => '/'.concat(location.pathname.split('/')[1]), [location.pathname])
  const isOperationPage = useMemo(() => pathname === OPERATIONS, [pathname])
  const isAuditPage = useMemo(() => pathname === AUDITS, [pathname])

  const classes = useStyles({ isOperationPage, isAuditPage })
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isOpenNotificationlist, setIsOpenNotificationList] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  notifications =
    notifications?.filter(
      asyncJobData =>
        asyncJobData?.type !== AsyncJobDataType.ON_WORKER_ROUTE_PRICING_CALCULATION &&
        asyncJobData?.type !== AsyncJobDataType.ON_WORKER_ROUTE_PRICING_CALCULATION_INITIAL,
    ) || []

  notifications =
    notifications?.filter(
      asyncJobData =>
        asyncJobData?.type !== AsyncJobDataType.ON_WORKER_ROUTE_PRICING_CALCULATION &&
        asyncJobData?.type !== AsyncJobDataType.ON_WORKER_ROUTE_PRICING_CALCULATION_INITIAL,
    ) || []

  notifications =
    notifications?.filter(
      asyncJobData =>
        asyncJobData?.type !== AsyncJobDataType.ON_WORKER_ROUTE_PRICING_CALCULATION &&
        asyncJobData?.type !== AsyncJobDataType.ON_WORKER_ROUTE_PRICING_CALCULATION_INITIAL,
    ) || []

  const sidebarMenuItems = useMemo(() => {
    switch (userRole) {
      case admin:
        return sidebarItems
      case manager:
        return sidebarItems.filter(item => MANAGER_SECTIONS.includes(item.section))
      case operations:
        return sidebarItems.filter(item => OPERATIONS_SECTIONS.includes(item.section))
      case warehouse:
        return sidebarItems.filter(item => WAREHOUSE_SECTIONS.includes(item.section))
      case auditor:
        return sidebarItems.filter(item => AUDITOR_SECTIONS.includes(item.section))
      case saver:
        return sidebarItems.filter(item => SAVER_SECTIONS.includes(item.section))
      default:
        return sidebarItems
    }
  }, [userRole])

  const logout = useCallback(async () => {
    Auth.signOut()

    await createAsyncAction(dispatch, actions.logout.request())

    navigate(MAIN)
  }, [navigate, dispatch])

  const handleClick = useCallback(() => {
    setCurrentDateRange(todayDateFilter)

    handleCloseSidebar()
  }, [setCurrentDateRange, handleCloseSidebar])

  const handleClickAway = useCallback(() => {
    setIsOpenNotificationList(false)
  }, [])

  const handleOpenNotificationList = useCallback(event => {
    setAnchorEl(event.currentTarget)
    setIsOpenNotificationList(prev => !prev)
  }, [])

  return (
    <div className={classes.root}>
      <div className={classes.main}>
        <div className={classes.logoWrapper}>
          <Link to={MAIN}>
            <Logo />
          </Link>
        </div>
        <div className={classes.linksList}>
          <div>
            {sidebarMenuItems.map((item, index) => (
              <NavLinkWrap
                onClick={handleClick}
                //@ts-ignore
                item={item}
                index={index}
                key={index}
              />
            ))}
          </div>
          <div>
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className={classes.notificationBlock}>
                <IconButton onClick={handleOpenNotificationList}>
                  <Badge badgeContent={notifications?.length} color="error">
                    <NotificationIcon />
                  </Badge>
                </IconButton>

                <Popper
                  open={isOpenNotificationlist}
                  anchorEl={anchorEl}
                  transition
                  placement={'bottom-end'}
                  className={classes.poppover}
                >
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps}>
                      <div className={classes.listWrap}>
                        <List className={classes.notificationList}>
                          {notifications?.length ? (
                            notifications?.map(asyncJobData => (
                              <NotificationListItem
                                key={asyncJobData?.id}
                                asyncJobData={asyncJobData}
                              />
                            ))
                          ) : (
                            <p className={classes.emptyState}>The Notification List Is Empty</p>
                          )}
                        </List>
                      </div>
                    </Fade>
                  )}
                </Popper>
              </div>
            </ClickAwayListener>
            <Link to={MAIN} className={clsx(classes.link, classes.logoutLink)} onClick={logout}>
              <UserIcon />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(Sidebar)
